import type { DepositChargeConfig } from "./types";

/**
 * Resolve the concrete date a deposit booking's balance is due.
 *
 * - `on_arrival`    → the session start (balance due at the session).
 * - `days_before`   → `daysBefore` CALENDAR days before the session start,
 *                     preserving the wall-clock time in `timeZone`. Subtracting a
 *                     fixed number of milliseconds instead would slip the due date
 *                     to the previous day across a DST change inside the window.
 * - `specific_date` → legacy mode; no concrete date was ever persisted, so it is
 *                     treated as `on_arrival`. Returning `null` here would leave the
 *                     booking with no `balanceDueDate` and the reminder cron (which
 *                     requires a non-null date) would never chase the balance.
 *
 * `timeZone` is an IANA zone (default "Europe/London", the platform default) used
 * only for the calendar-day subtraction. Returns `null` for a genuinely unknown mode.
 */
export function resolveBalanceDueDate(
  config: DepositChargeConfig,
  sessionStart: Date,
  timeZone: string = "Europe/London"
): Date | null {
  // `remainderDue` is typed to the current modes, but legacy tickets can still
  // carry the removed "specific_date" value in the DB — compare as a string.
  switch (config.remainderDue as string) {
    case "on_arrival":
    case "specific_date":
      return sessionStart;
    case "days_before":
      return shiftCalendarDays(sessionStart, -(config.daysBefore ?? 0), timeZone);
    default:
      return null;
  }
}

/**
 * Shift `from` by `days` CALENDAR days (negative = back) preserving the
 * wall-clock time in `timeZone` — correct for any UTC offset change (DST)
 * between `from` and the result. A fixed-milliseconds shift would slip the
 * date to the previous/next day across a DST change inside the window.
 * Shared by `resolveBalanceDueDate` (legacy 2-leg) and
 * `resolveInstalmentDueDate` (N-instalment plans).
 */
export function shiftCalendarDays(
  from: Date,
  days: number,
  timeZone: string = "Europe/London"
): Date {
  const naive = new Date(from.getTime() + days * 24 * 60 * 60 * 1000);
  const drift = tzOffsetMs(from, timeZone) - tzOffsetMs(naive, timeZone);
  return new Date(naive.getTime() + drift);
}

/**
 * UTC offset (ms) of `date` in `timeZone` at that instant, via `Intl` — no external
 * tz library, so this stays cheap in the widget / webcomponents bundles. Positive
 * east of UTC (e.g. +3_600_000 for London during BST).
 */
function tzOffsetMs(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUTC = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second")
  );
  return asUTC - date.getTime();
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The calendar day (`YYYY-MM-DD`) of `date` in `timeZone`. The counterpart of
 * `endOfCalendarDay`: both sides of a "is this day before today / after the
 * session day" comparison must be reduced to a day in the SAME zone, or a
 * late-evening instant lands on different days depending on where it is read.
 */
export function calendarDayInTimeZone(
  date: Date,
  timeZone: string = "Europe/London"
): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/**
 * The last instant (23:59:59.999 wall-clock) of the calendar day `isoDay`
 * (`YYYY-MM-DD`) in `timeZone`, or `null` for a malformed or impossible day.
 *
 * A staff-chosen balance due DAY is stored this way so the customer has the
 * whole day to pay: the reminder cron's overdue pass selects
 * `balanceDueDate < now`, and the dashboard flags "overdue" the same way, so
 * a start-of-day instant would mark the booking overdue at 00:01 on the day
 * it falls due. The zone defaults to Europe/London for consistency with
 * `resolveBalanceDueDate`, which every other writer of `balanceDueDate` uses.
 */
export function endOfCalendarDay(
  isoDay: string,
  timeZone: string = "Europe/London"
): Date | null {
  const m = ISO_DAY.exec(isoDay ?? "");
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  // Reject impossible days (e.g. 2026-02-30 would silently roll to March).
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (
    probe.getUTCFullYear() !== y ||
    probe.getUTCMonth() !== mo - 1 ||
    probe.getUTCDate() !== d
  ) {
    return null;
  }
  // Wall-clock end of day as if it were UTC, then remove the zone's offset AT
  // THAT instant. One correction pass is enough: the offset can only differ
  // from the guess's if a DST change sits within the offset's own width of
  // midnight, and the second pass settles it.
  // The offset probe runs at whole-second precision (`tzOffsetMs` reads
  // Intl parts, which carry no milliseconds); the .999 is added at the end.
  const wallClock = Date.UTC(y, mo - 1, d, 23, 59, 59);
  let result = new Date(wallClock - tzOffsetMs(new Date(wallClock), timeZone));
  result = new Date(wallClock - tzOffsetMs(result, timeZone));
  return new Date(result.getTime() + 999);
}

/**
 * Bring a proposed balance due DAY inside the window staff may choose from:
 * no later than the session day, and — while the session is still ahead —
 * no earlier than today. For a session already in the past the earlier bound
 * is dropped: the balance genuinely fell due back then, and refusing every
 * day would make a late-recorded deposit booking impossible to enter.
 * The same rule the API validates and the wizard's calendar disables, applied
 * to a DEFAULT (e.g. a "7 days before" config for a session 3 days away
 * resolves to 4 days ago; the default becomes today instead).
 */
export function clampBalanceDueDay(args: {
  day: string;
  today: string;
  sessionDay?: string | null;
}): string {
  let day = args.day;
  if (args.sessionDay && day > args.sessionDay) day = args.sessionDay;
  const sessionAhead = !args.sessionDay || args.sessionDay >= args.today;
  if (sessionAhead && day < args.today) day = args.today;
  return day;
}
